import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import CheckIcon from '@material-ui/icons/Check';
import SearchIcon from '@material-ui/icons/Search';
import WarningIcon from '@material-ui/icons/Warning';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import ScheduleIcon from '@material-ui/icons/Schedule';
import RefreshIcon from '@material-ui/icons/Refresh';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ShoppingBasketIcon from '@material-ui/icons/ShoppingBasket';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import StoreIcon from '@material-ui/icons/Store';

import useApi from '../../../utils/useApi';
import { formattedCurrency } from '../../../utils/utils';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(1),
    width: '100%',
    boxSizing: 'border-box',
    [theme.breakpoints.down('xs')]: {
      padding: '4px',
    },
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '240px',
    gap: theme.spacing(2),
  },

  // ===== TOP KPIS (MÉTRICAS - IDÊNTICO À CURVA ABC) =====
  kpiGrid: {
    marginBottom: theme.spacing(1.5),
  },
  kpiCard: {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    padding: theme.spacing(1, 2),
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    boxSizing: 'border-box',
    height: '100%',
  },
  kpiIconWrap: {
    padding: '8px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
  },
  kpiLabel: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    fontWeight: 500,
    lineHeight: 1.2,
  },
  kpiValue: {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: '#1e293b',
    lineHeight: 1.2,
  },

  // ===== FILTROS E BUSCA (IDÊNTICO À CURVA ABC) =====
  filterContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    width: '100%',
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
  },
  searchField: {
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '280px',
    },
  },
  filterButtonGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  },
  filterButton: {
    borderRadius: '20px',
    textTransform: 'none',
    fontSize: '0.76rem',
    padding: '3px 12px',
    height: '30px',
  },

  // ===== LISTA E CARDS DE FORNECEDORES (PADRÃO CURVA ABC) =====
  supplierCardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  supplierCard: {
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    boxShadow: 'none',
    overflow: 'hidden',
    backgroundColor: '#ffffff',
    marginBottom: '10px',
    transition: 'border-color 0.15s ease',
    '&:hover': {
      borderColor: '#cbd5e1',
    },
  },
  cardContentInner: {
    padding: '12px 14px !important',
    '&:last-child': {
      paddingBottom: '12px !important',
    },
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '10px',
  },
  supplierTitle: {
    fontWeight: 600,
    fontSize: '0.92rem',
    color: '#1e293b',
    lineHeight: 1.3,
  },
  supplierSubtitle: {
    color: '#94a3b8',
    fontSize: '0.74rem',
    marginTop: '2px',
  },
  cardMetrics: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px',
    padding: '10px 0',
    borderTop: '1px solid #f1f5f9',
    borderBottom: '1px solid #f1f5f9',
  },
  metricItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  metricLabel: {
    fontSize: '0.68rem',
    color: '#94a3b8',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  metricValue: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#1e293b',
  },
  cardFooter: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '10px',
  },
  actionBtn: {
    flex: 1,
    borderRadius: '8px',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.76rem',
    height: '34px',
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
  },
  copyBtn: {
    borderColor: '#cbd5e1',
    color: '#334155',
    backgroundColor: '#ffffff',
    '&:hover': {
      backgroundColor: '#f8fafc',
      borderColor: '#94a3b8',
    },
  },
  expandBtn: {
    flex: 1,
    borderRadius: '8px',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.76rem',
    height: '34px',
    color: '#2563eb',
    backgroundColor: '#eff6ff',
    border: '1px solid #dbeafe',
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    '&:hover': {
      backgroundColor: '#dbeafe',
    },
  },
  whatsappBtn: {
    backgroundColor: '#25d366',
    color: '#ffffff',
    flex: '0 0 auto',
    minWidth: '36px',
    width: '36px',
    height: '34px',
    padding: 0,
    borderRadius: '8px',
    boxSizing: 'border-box',
    '&:hover': {
      backgroundColor: '#1eb857',
    },
  },

  // Status Chips (idênticos ao padrão da Curva ABC)
  statusUrgent: {
    backgroundColor: '#fef2f2',
    color: '#ef4444',
    border: '1px solid #fecaca',
    fontWeight: 600,
    fontSize: '0.7rem',
    height: '22px',
  },
  statusWarning: {
    backgroundColor: '#fffbeb',
    color: '#f59e0b',
    border: '1px solid #fde68a',
    fontWeight: 600,
    fontSize: '0.7rem',
    height: '22px',
  },
  statusOk: {
    backgroundColor: '#f0fdf4',
    color: '#22c55e',
    border: '1px solid #bbf7d0',
    fontWeight: 600,
    fontSize: '0.7rem',
    height: '22px',
  },
  statusInfo: {
    backgroundColor: '#eff6ff',
    color: '#3b82f6',
    border: '1px solid #bfdbfe',
    fontWeight: 600,
    fontSize: '0.7rem',
    height: '22px',
  },

  // ===== LISTA DE ITENS EXPANDIDA =====
  expandedContainer: {
    borderTop: '1px solid #e2e8f0',
    backgroundColor: '#f8fafc',
    padding: '8px',
    [theme.breakpoints.down('xs')]: {
      padding: '4px',
    },
  },
  // Tabela Desktop
  table: {
    minWidth: 700,
    backgroundColor: '#ffffff',
  },
  tableHeaderCell: {
    backgroundColor: '#f1f5f9',
    color: '#334155',
    fontWeight: 700,
    fontSize: '0.72rem',
    padding: '6px 10px',
    borderBottom: '1px solid #e2e8f0',
  },
  tableBodyCell: {
    fontSize: '0.78rem',
    padding: '6px 10px',
    borderBottom: '1px solid #f1f5f9',
  },
  // Item Mobile Card
  mobileItemBox: {
    backgroundColor: '#ffffff',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    padding: '8px 10px',
    marginBottom: '4px',
  },
  badgeA: {
    backgroundColor: '#dbeafe',
    color: '#1e40af',
    fontWeight: 700,
    fontSize: '0.65rem',
    height: '18px',
  },
  badgeB: {
    backgroundColor: '#fef3c7',
    color: '#92400e',
    fontWeight: 700,
    fontSize: '0.65rem',
    height: '18px',
  },
  badgeC: {
    backgroundColor: '#f1f5f9',
    color: '#475569',
    fontWeight: 700,
    fontSize: '0.65rem',
    height: '18px',
  },
  suggestedChip: {
    backgroundColor: '#eff6ff',
    color: '#1d4ed8',
    fontWeight: 700,
    fontSize: '0.74rem',
    height: '20px',
    borderRadius: '4px',
  },
  emptyBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4, 2),
    textAlign: 'center',
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    border: '1px dashed #cbd5e1',
    marginTop: theme.spacing(1),
  },
}));

const StockReplenishmentView = ({ stockId, mobile }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('xs'));
  const isMobileLayout = mobile || isXs;

  // API Call
  const [loadReplenishment, replenishmentInfo] = useApi({
    method: 'get',
    url: '/stock/replenishment',
  });

  const fetchData = useCallback(() => {
    loadReplenishment({
      params: stockId ? { stockId } : {},
    });
  }, [stockId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const rawData = replenishmentInfo.data || {};
  const suppliers = rawData.suppliers || [];
  const loading = replenishmentInfo.loading;

  // Estados locais
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL'); // 'ALL' | 'DUE_THIS_WEEK' | 'RUPTURE' | 'FUTURE'
  const [copiedSupplierKey, setCopiedSupplierKey] = useState(null);

  // Todos os fornecedores retraídos por padrão
  const [expandedCards, setExpandedCards] = useState({});

  const handleToggleCard = (key) => {
    setExpandedCards((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Copiar lista de compras
  const handleCopyShoppingList = (sKey, text) => {
    if (!text) return;
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text);
    } else {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
    setCopiedSupplierKey(sKey);
    setTimeout(() => {
      setCopiedSupplierKey(null);
    }, 2500);
  };

  // Abrir WhatsApp
  const handleOpenWhatsApp = (whatsappNumber, text) => {
    if (!whatsappNumber) return;
    const cleanPhone = whatsappNumber.replace(/\D/g, '');
    const fullPhone = cleanPhone.length <= 11 ? `55${cleanPhone}` : cleanPhone;
    const encodedText = encodeURIComponent(text || '');
    window.open(`https://api.whatsapp.com/send?phone=${fullPhone}&text=${encodedText}`, '_blank');
  };

  // Métricas Consolidadas
  const metrics = useMemo(() => {
    let countRuptureProducts = 0;
    let countSuppliersWithRupture = 0;
    let countDueThisWeek = 0;
    let countFutureSuppliers = 0;
    let totalEstimatedValue = 0;
    let totalSuggestedItems = 0;

    suppliers.forEach((s) => {
      totalEstimatedValue += s.total_estimated_value || 0;
      totalSuggestedItems += s.total_suggested_items || 0;
      if (s.cycle_status === 'DUE_THIS_WEEK' || s.cycle_status === 'OVERDUE') {
        countDueThisWeek += 1;
      }
      if (s.cycle_status === 'FUTURE' || s.cycle_status === 'RECENTLY_REPLENISHED') {
        countFutureSuppliers += 1;
      }
      if (s.has_rupture) {
        countSuppliersWithRupture += 1;
      }
      (s.products || []).forEach((p) => {
        if (p.current_stock <= 0) {
          countRuptureProducts += 1;
        }
      });
    });

    return {
      totalCriticalProducts: rawData.total_critical_products || 0,
      countRuptureProducts,
      countSuppliersWithRupture,
      totalSuppliers: suppliers.length,
      countDueThisWeek,
      countFutureSuppliers,
      totalEstimatedValue,
      totalSuggestedItems,
    };
  }, [suppliers, rawData]);

  // Filtragem de Fornecedores
  const filteredSuppliers = useMemo(() => {
    return suppliers.filter((s) => {
      if (statusFilter === 'DUE_THIS_WEEK') {
        if (s.cycle_status !== 'DUE_THIS_WEEK' && s.cycle_status !== 'OVERDUE') return false;
      } else if (statusFilter === 'RUPTURE') {
        if (!s.has_rupture) return false;
      } else if (statusFilter === 'FUTURE') {
        if (s.cycle_status !== 'FUTURE' && s.cycle_status !== 'RECENTLY_REPLENISHED') return false;
      }

      if (searchTerm.trim()) {
        const lower = searchTerm.toLowerCase();
        const matchSupplierName = (s.supplier_name || '').toLowerCase().includes(lower);
        const matchProduct = (s.products || []).some((p) =>
          (p.name || '').toLowerCase().includes(lower) || (p.id && p.id.toString().includes(lower))
        );
        return matchSupplierName || matchProduct;
      }

      return true;
    });
  }, [suppliers, statusFilter, searchTerm]);

  // Cor da Borda do Card pelo Ciclo
  const getCycleBorderColor = (cycleStatus) => {
    if (cycleStatus === 'DUE_THIS_WEEK') return '#ea580c'; // Laranja: urgente
    if (cycleStatus === 'OVERDUE') return '#dc2626'; // Vermelho: atrasado
    if (cycleStatus === 'RECENTLY_REPLENISHED') return '#16a34a'; // Verde: recente
    return '#3b82f6'; // Azul: futuro
  };

  const getCycleStatusText = (cycleStatus, daysUntilNext, nextDate) => {
    if (cycleStatus === 'DUE_THIS_WEEK') {
      return `Pedir esta semana (${daysUntilNext === 0 ? 'Hoje' : `em ${daysUntilNext}d`})`;
    }
    if (cycleStatus === 'OVERDUE') {
      return 'Ciclo em atraso';
    }
    if (cycleStatus === 'RECENTLY_REPLENISHED') {
      return 'Comprado recentemente';
    }
    return daysUntilNext ? `Próximo em ${daysUntilNext}d` : `Previsão: ${nextDate || 'Futuro'}`;
  };

  // Helpers Visuais (alinhados com a Curva ABC)
  const getCycleChip = (s, ruptureCount) => {
    if (s.has_rupture) {
      return (
        <Chip
          label={ruptureCount > 0 ? `${ruptureCount} em Ruptura` : 'Ruptura'}
          size="small"
          className={classes.statusUrgent}
        />
      );
    }
    if (s.cycle_status === 'DUE_THIS_WEEK') {
      return (
        <Chip
          label={`Pedir esta semana (${s.days_until_next === 0 ? 'Hoje' : `em ${s.days_until_next}d`})`}
          size="small"
          className={classes.statusWarning}
        />
      );
    }
    if (s.cycle_status === 'OVERDUE') {
      return (
        <Chip
          label="Ciclo em atraso"
          size="small"
          className={classes.statusUrgent}
        />
      );
    }
    if (s.cycle_status === 'RECENTLY_REPLENISHED') {
      return (
        <Chip
          label="Comprado recentemente"
          size="small"
          className={classes.statusOk}
        />
      );
    }
    return (
      <Chip
        label={s.days_until_next ? `Próximo em ${s.days_until_next}d` : 'Ciclo futuro'}
        size="small"
        className={classes.statusInfo}
      />
    );
  };

  const getCurvaBadge = (curva) => {
    if (curva === 'A') return <Chip label="A" size="small" className={classes.badgeA} />;
    if (curva === 'B') return <Chip label="B" size="small" className={classes.badgeB} />;
    return <Chip label="C" size="small" className={classes.badgeC} />;
  };

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress size={32} style={{ color: '#2563eb' }} />
        <Typography variant="body2" style={{ color: '#64748b', fontWeight: 500, marginTop: 6, fontSize: '0.82rem' }}>
          Calculando frequências de compras e sugestões...
        </Typography>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      {/* 1. CARDS DE MÉTRICAS (IDÊNTICO À CURVA ABC: xs={12} sm={6} md={3}) */}
      <Grid id="tour-replenishment-kpis" container spacing={isMobileLayout ? 1 : 2} className={classes.kpiGrid}>
        <Grid item xs={12} sm={6} md={3}>
          <div className={classes.kpiCard} style={{ borderColor: '#fecaca', borderLeft: '4px solid #ef4444' }}>
            <div className={classes.kpiIconWrap} style={{ backgroundColor: '#fef2f2' }}>
              <ErrorOutlineIcon style={{ color: '#ef4444' }} />
            </div>
            <div className={classes.cardContent}>
              <Typography className={classes.kpiLabel}>Com Ruptura</Typography>
              <Typography className={classes.kpiValue} style={{ color: '#ef4444' }}>
                {metrics.countSuppliersWithRupture}
              </Typography>
            </div>
          </div>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <div className={classes.kpiCard} style={{ borderColor: '#fed7aa', borderLeft: '4px solid #f59e0b' }}>
            <div className={classes.kpiIconWrap} style={{ backgroundColor: '#fffbeb' }}>
              <ScheduleIcon style={{ color: '#f59e0b' }} />
            </div>
            <div className={classes.cardContent}>
              <Typography className={classes.kpiLabel}>Pedir Desta Semana</Typography>
              <Typography className={classes.kpiValue} style={{ color: '#d97706' }}>
                {metrics.countDueThisWeek} ped.
              </Typography>
            </div>
          </div>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <div className={classes.kpiCard}>
            <div className={classes.kpiIconWrap} style={{ backgroundColor: '#eff6ff' }}>
              <ShoppingBasketIcon style={{ color: '#3b82f6' }} />
            </div>
            <div className={classes.cardContent}>
              <Typography className={classes.kpiLabel}>Total de Itens</Typography>
              <Typography className={classes.kpiValue} style={{ color: '#2563eb' }}>
                {metrics.totalCriticalProducts}
              </Typography>
            </div>
          </div>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <div className={classes.kpiCard}>
            <div className={classes.kpiIconWrap} style={{ backgroundColor: '#ecfdf5' }}>
              <TrendingUpIcon style={{ color: '#10b981' }} />
            </div>
            <div className={classes.cardContent}>
              <Typography className={classes.kpiLabel}>Investimento Total</Typography>
              <Typography className={classes.kpiValue} style={{ color: '#15803d' }}>
                {formattedCurrency(metrics.totalEstimatedValue)}
              </Typography>
            </div>
          </div>
        </Grid>
      </Grid>

      {/* 2. FILTROS E BUSCA (IDÊNTICO À CURVA ABC) */}
      <div className={classes.filterContainer}>
        <TextField
          id="tour-replenishment-search"
          size="small"
          variant="outlined"
          placeholder="Buscar fornecedor ou produto..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={classes.searchField}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon style={{ color: '#94a3b8', fontSize: '1.1rem' }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title="Recarregar sugestões">
                  <IconButton size="small" onClick={fetchData} style={{ color: '#64748b' }}>
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
            style: { padding: '4px 8px' },
          }}
        />
        <div id="tour-replenishment-filters" className={classes.filterButtonGroup}>
          <Button
            size="small"
            variant={statusFilter === 'ALL' ? 'contained' : 'outlined'}
            color="primary"
            onClick={() => setStatusFilter('ALL')}
            className={classes.filterButton}
          >
            Todos ({suppliers.length})
          </Button>
          <Button
            size="small"
            variant={statusFilter === 'DUE_THIS_WEEK' ? 'contained' : 'outlined'}
            onClick={() => setStatusFilter('DUE_THIS_WEEK')}
            className={classes.filterButton}
            style={{
              borderColor: '#ea580c',
              color: statusFilter === 'DUE_THIS_WEEK' ? '#fff' : '#ea580c',
              backgroundColor: statusFilter === 'DUE_THIS_WEEK' ? '#ea580c' : 'transparent',
            }}
          >
            ⚡ Pedir Esta Semana ({metrics.countDueThisWeek})
          </Button>
          <Button
            size="small"
            variant={statusFilter === 'RUPTURE' ? 'contained' : 'outlined'}
            onClick={() => setStatusFilter('RUPTURE')}
            className={classes.filterButton}
            style={{
              borderColor: '#ef4444',
              color: statusFilter === 'RUPTURE' ? '#fff' : '#ef4444',
              backgroundColor: statusFilter === 'RUPTURE' ? '#ef4444' : 'transparent',
            }}
          >
            🚨 Ruptura ({metrics.countSuppliersWithRupture})
          </Button>
          <Button
            size="small"
            variant={statusFilter === 'FUTURE' ? 'contained' : 'outlined'}
            onClick={() => setStatusFilter('FUTURE')}
            className={classes.filterButton}
            style={{
              borderColor: '#3b82f6',
              color: statusFilter === 'FUTURE' ? '#fff' : '#3b82f6',
              backgroundColor: statusFilter === 'FUTURE' ? '#3b82f6' : 'transparent',
            }}
          >
            📅 Próximos Ciclos ({metrics.countFutureSuppliers})
          </Button>
        </div>
      </div>

      {/* 4. LISTA DE FORNECEDORES (CARDS DEDICADOS COM BORDA DE CICLO) */}
      {filteredSuppliers.length === 0 ? (
        <div className={classes.emptyBox}>
          <CheckCircleOutlineIcon style={{ fontSize: 44, color: '#10b981', marginBottom: 8 }} />
          <Typography variant="h6" style={{ color: '#0f172a', fontWeight: 600, fontSize: '0.92rem' }}>
            Tudo sob controle no estoque!
          </Typography>
          <Typography variant="body2" style={{ color: '#64748b', maxWidth: '420px', marginTop: 4, fontSize: '0.78rem' }}>
            {searchTerm || statusFilter !== 'ALL'
              ? 'Nenhum fornecedor ou produto encontrado para os filtros selecionados.'
              : 'Nenhum produto em ponto de ruptura ou necessidade de compra para os ciclos atuais.'}
          </Typography>
          {(searchTerm || statusFilter !== 'ALL') && (
            <Button
              size="small"
              variant="text"
              color="primary"
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('ALL');
              }}
              style={{ marginTop: 6, textTransform: 'none', fontWeight: 600, fontSize: '0.78rem' }}
            >
              Limpar filtros
            </Button>
          )}
        </div>
      ) : (
        <div className={classes.supplierCardList}>
          {filteredSuppliers.map((s, idx) => {
            const sKey = s.supplier_id ? s.supplier_id.toString() : `unassigned_${idx}`;
            const isExpanded = !!expandedCards[sKey];
            const isCopied = copiedSupplierKey === sKey;
            const prods = s.products || [];
            const ruptureItems = prods.filter((p) => p.current_stock <= 0);
            const borderColor = getCycleBorderColor(s.cycle_status);

            return (
              <Card
                key={sKey}
                id={idx === 0 ? 'tour-replenishment-card' : undefined}
                className={classes.supplierCard}
                style={{ borderLeft: `4px solid ${borderColor}` }}
              >
                <CardContent className={classes.cardContentInner}>
                  {/* HEADER (IDÊNTICO À CURVA ABC: NOME + CHIP) */}
                  <div className={classes.cardHeader}>
                    <div style={{ flex: 1, minWidth: 0, marginRight: 8 }}>
                      <Typography className={classes.supplierTitle} noWrap>
                        {s.supplier_name || 'Sem Fornecedor'}
                      </Typography>
                      <Typography className={classes.supplierSubtitle}>
                        {s.supplier_whatsapp ? `WhatsApp: ${s.supplier_whatsapp}` : 'Fornecedor Cadastrado'}
                      </Typography>
                    </div>
                    {getCycleChip(s, ruptureItems.length)}
                  </div>

                  {/* MÉTRICAS EM GRID 2x2 (MESMO PADRÃO E ESPAÇAMENTO DA CURVA ABC) */}
                  <div className={classes.cardMetrics}>
                    <div className={classes.metricItem}>
                      <Typography className={classes.metricLabel}>Frequência Ciclo</Typography>
                      <Typography variant="body2" className={classes.metricValue}>
                        A cada {s.avg_interval_days || 30} dias
                      </Typography>
                    </div>

                    <div className={classes.metricItem}>
                      <Typography className={classes.metricLabel}>Previsão</Typography>
                      <Typography
                        variant="body2"
                        className={classes.metricValue}
                        style={{ color: borderColor, fontWeight: 700 }}
                      >
                        {getCycleStatusText(s.cycle_status, s.days_until_next, s.next_replenishment_date)}
                      </Typography>
                    </div>

                    <div className={classes.metricItem}>
                      <Typography className={classes.metricLabel}>Itens no Pedido</Typography>
                      <Typography variant="body2" className={classes.metricValue}>
                        {prods.length} {prods.length === 1 ? 'produto' : 'produtos'} ({s.total_suggested_items || 0} un.)
                      </Typography>
                    </div>

                    <div className={classes.metricItem}>
                      <Typography className={classes.metricLabel}>Total Estimado</Typography>
                      <Typography
                        variant="body2"
                        className={classes.metricValue}
                        style={{ color: '#16a34a', fontWeight: 700 }}
                      >
                        {formattedCurrency(s.total_estimated_value || 0)}
                      </Typography>
                    </div>
                  </div>

                  {/* AÇÕES NO RODAPÉ DO CARD */}
                  <div
                    id={idx === 0 ? 'tour-replenishment-actions' : undefined}
                    className={classes.cardFooter}
                  >
                    {s.supplier_whatsapp && (
                      <Button
                        size="small"
                        variant="contained"
                        className={classes.whatsappBtn}
                        onClick={() => handleOpenWhatsApp(s.supplier_whatsapp, s.shopping_list_text)}
                        title="Enviar pelo WhatsApp"
                      >
                        <WhatsAppIcon style={{ fontSize: '1.1rem' }} />
                      </Button>
                    )}

                    <Button
                      size="small"
                      variant="outlined"
                      className={`${classes.actionBtn} ${classes.copyBtn}`}
                      startIcon={isCopied ? <CheckIcon style={{ color: '#16a34a', fontSize: '0.88rem' }} /> : <FileCopyIcon style={{ fontSize: '0.88rem' }} />}
                      onClick={() => handleCopyShoppingList(sKey, s.shopping_list_text)}
                    >
                      {isCopied ? 'Copiado!' : 'Copiar Pedido'}
                    </Button>

                    <Button
                      size="small"
                      className={classes.expandBtn}
                      endIcon={
                        <ExpandMoreIcon
                          style={{
                            transform: isExpanded ? 'rotate(180deg)' : 'none',
                            transition: 'transform 0.2s',
                            fontSize: '1rem',
                          }}
                        />
                      }
                      onClick={() => handleToggleCard(sKey)}
                    >
                      {isExpanded ? 'Ocultar itens' : `Ver itens (${prods.length})`}
                    </Button>
                  </div>
                </CardContent>

                {/* DETALHES DE PRODUTOS (EXPANSÍVEL) */}
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <div className={classes.expandedContainer}>
                  {isMobileLayout ? (
                    // ITENS MOBILE
                    <div>
                      {prods.map((p) => (
                        <div key={p.id} className={classes.mobileItemBox}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 6 }}>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <Typography variant="body2" style={{ fontWeight: 600, color: '#0f172a', fontSize: '0.8rem' }}>
                                #{p.id} - {p.name}
                              </Typography>
                              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 3 }}>
                                {getCurvaBadge(p.curva)}
                                {p.current_stock <= 0 ? (
                                  <span style={{ color: '#dc2626', fontWeight: 700, fontSize: '0.72rem' }}>🚨 Esgotado</span>
                                ) : (
                                  <span style={{ color: '#64748b', fontSize: '0.72rem' }}>Estoque: {p.current_stock} un</span>
                                )}
                              </div>
                            </div>
                            <Chip
                              label={`Comprar: ${p.suggested_quantity} un`}
                              size="small"
                              className={classes.suggestedChip}
                            />
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: '0.74rem', color: '#64748b' }}>
                            <span>Unit: {formattedCurrency(p.unit_cost)}</span>
                            <span>Subtotal: <strong style={{ color: '#15803d' }}>{formattedCurrency(p.estimated_cost)}</strong></span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    // TABELA DESKTOP
                    <TableContainer component={Paper} elevation={0} style={{ borderRadius: 6, maxHeight: '420px', overflow: 'auto' }}>
                      <Table size="small" stickyHeader className={classes.table}>
                        <TableHead>
                          <TableRow>
                            <TableCell className={classes.tableHeaderCell} style={{ width: 65 }}>Cód</TableCell>
                            <TableCell className={classes.tableHeaderCell}>Produto</TableCell>
                            <TableCell className={classes.tableHeaderCell} align="center" style={{ width: 65 }}>Curva</TableCell>
                            <TableCell className={classes.tableHeaderCell} align="right" style={{ width: 105 }}>Estoque Atual</TableCell>
                            <TableCell className={classes.tableHeaderCell} align="right" style={{ width: 90 }}>Cobertura</TableCell>
                            <TableCell className={classes.tableHeaderCell} align="right" style={{ width: 90 }}>Giro/Mês</TableCell>
                            <TableCell className={classes.tableHeaderCell} align="right" style={{ width: 130 }}>Qtd Sugerida</TableCell>
                            <TableCell className={classes.tableHeaderCell} align="right" style={{ width: 105 }}>Custo Unit.</TableCell>
                            <TableCell className={classes.tableHeaderCell} align="right" style={{ width: 115 }}>Subtotal</TableCell>
                            <TableCell className={classes.tableHeaderCell} style={{ width: 150 }}>Diagnóstico</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {prods.map((p) => {
                            const isZero = p.current_stock <= 0;
                            return (
                              <TableRow key={p.id} hover style={{ backgroundColor: isZero ? '#fff5f5' : undefined }}>
                                <TableCell className={classes.tableBodyCell} style={{ color: '#64748b', fontWeight: 500 }}>
                                  #{p.id}
                                </TableCell>
                                <TableCell className={classes.tableBodyCell} style={{ fontWeight: 600, color: '#0f172a' }}>
                                  {p.name}
                                </TableCell>
                                <TableCell className={classes.tableBodyCell} align="center">
                                  {getCurvaBadge(p.curva)}
                                </TableCell>
                                <TableCell
                                  className={classes.tableBodyCell}
                                  align="right"
                                  style={{ fontWeight: 700, color: isZero ? '#dc2626' : '#334155' }}
                                >
                                  {p.current_stock} un
                                </TableCell>
                                <TableCell className={classes.tableBodyCell} align="right" style={{ color: '#64748b' }}>
                                  {p.coverage_days > 0 ? `${p.coverage_days}d` : '0d'}
                                </TableCell>
                                <TableCell className={classes.tableBodyCell} align="right" style={{ color: '#64748b' }}>
                                  ~{p.monthly_sales} un
                                </TableCell>
                                <TableCell className={classes.tableBodyCell} align="right">
                                  <Chip
                                    label={`${p.suggested_quantity} un`}
                                    size="small"
                                    className={classes.suggestedChip}
                                  />
                                </TableCell>
                                <TableCell className={classes.tableBodyCell} align="right" style={{ color: '#64748b' }}>
                                  {formattedCurrency(p.unit_cost)}
                                </TableCell>
                                <TableCell
                                  className={classes.tableBodyCell}
                                  align="right"
                                  style={{ fontWeight: 700, color: '#15803d' }}
                                >
                                  {formattedCurrency(p.estimated_cost)}
                                </TableCell>
                                <TableCell className={classes.tableBodyCell}>
                                  {isZero ? (
                                    <span style={{ color: '#dc2626', fontWeight: 700 }}>🚨 RUPTURA</span>
                                  ) : (
                                    <span style={{ color: '#2563eb', fontWeight: 500 }}>📦 Repor</span>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </div>
              </Collapse>
            </Card>
          );
        })}
      </div>
    )}
  </div>
);
};

export default StockReplenishmentView;
